import React, { useState } from 'react';
import {
  Table, Tag, Select, Button, Modal, Input, message, Space, Empty, Alert, Tabs,
} from 'antd';
import {
  useListCasesQuery,
  useTakeActionMutation,
  useListAppealsQuery,
  useReviewAppealMutation,
} from '../services/adminApi';
import AdminPageHeader, { adminPanelStyle } from './AdminPageHeader';

const { TextArea } = Input;

const STATUS_COLORS = {
  open: 'blue',
  investigating: 'orange',
  resolved: 'green',
  pending: 'orange',
  approved: 'green',
  denied: 'red',
};

const ACTION_OPTIONS = [
  { value: 'approve', label: 'Approve content' },
  { value: 'dismiss', label: 'Dismiss report' },
  { value: 'warn', label: 'Warn user' },
  { value: 'remove_content', label: 'Remove content' },
  { value: 'suspend', label: 'Suspend user' },
  { value: 'ban_user', label: 'Ban user' },
  { value: 'escalate', label: 'Escalate' },
];

function CasesPanel() {
  const { data, isLoading, isError, error } = useListCasesQuery();
  const [takeAction, { isLoading: acting }] = useTakeActionMutation();
  const [actionModal, setActionModal] = useState(null);

  const handleAction = async () => {
    try {
      await takeAction({
        caseId: actionModal.caseId,
        action: actionModal.action,
        reason: actionModal.reason,
      }).unwrap();
      message.success('Action recorded');
      setActionModal(null);
    } catch (err) {
      message.error(err.data?.title || 'Failed to take action');
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 70 },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => <Tag color={STATUS_COLORS[status]}>{status}</Tag>,
    },
    {
      title: 'Subject',
      dataIndex: 'subject_type',
      key: 'subject_type',
      width: 100,
      render: (type) => <Tag>{type}</Tag>,
    },
    { title: 'Reason', dataIndex: 'reason', key: 'reason', width: 150 },
    { title: 'Reporter', dataIndex: 'reporter_name', key: 'reporter_name', width: 150 },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      width: 90,
      render: (p) => (
        <Tag color={p === 'high' ? 'red' : p === 'medium' || p === 'normal' ? 'orange' : 'blue'}>
          {p}
        </Tag>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 170,
      render: (d) => (d ? new Date(d).toLocaleString() : '—'),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 140,
      render: (_, record) => (
        <Button
          size="small"
          type="primary"
          disabled={record.status === 'resolved'}
          onClick={() => setActionModal({ caseId: record.id, action: 'dismiss', reason: '' })}
        >
          Take Action
        </Button>
      ),
    },
  ];

  return (
    <>
      {isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={error?.data?.title || 'Failed to load moderation cases'}
        />
      )}
      <div style={adminPanelStyle}>
        <Table
          dataSource={data?.items}
          columns={columns}
          loading={isLoading}
          rowKey="id"
          pagination={{ pageSize: 20 }}
          size="middle"
          locale={{ emptyText: <Empty description="No moderation cases" /> }}
        />
      </div>
      <Modal
        title="Take Moderation Action"
        open={!!actionModal}
        onOk={handleAction}
        onCancel={() => setActionModal(null)}
        confirmLoading={acting}
        okText="Submit Action"
      >
        <Space direction="vertical" style={{ width: '100%' }} size={16}>
          <div>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Action</div>
            <Select
              style={{ width: '100%' }}
              value={actionModal?.action}
              onChange={(value) => setActionModal({ ...actionModal, action: value })}
              options={ACTION_OPTIONS}
            />
          </div>
          <div>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Reason (optional)</div>
            <TextArea
              rows={3}
              value={actionModal?.reason || ''}
              onChange={(e) => setActionModal({ ...actionModal, reason: e.target.value })}
              placeholder="Add a note about this action..."
            />
          </div>
        </Space>
      </Modal>
    </>
  );
}

function AppealsPanel() {
  const [statusFilter, setStatusFilter] = useState('pending');
  const { data, isLoading, isError, error } = useListAppealsQuery({
    status: statusFilter || undefined,
    limit: 50,
  });
  const [reviewAppeal, { isLoading: reviewing }] = useReviewAppealMutation();
  const [reviewModal, setReviewModal] = useState(null);

  const handleReview = async () => {
    try {
      await reviewAppeal({
        appealId: reviewModal.appealId,
        status: reviewModal.status,
        reviewNotes: reviewModal.notes,
      }).unwrap();
      message.success(`Appeal ${reviewModal.status}`);
      setReviewModal(null);
    } catch (err) {
      message.error(err.data?.title || 'Failed to review appeal');
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 70 },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (s) => <Tag color={STATUS_COLORS[s]}>{s}</Tag>,
    },
    {
      title: 'Appellant',
      key: 'appellant',
      width: 160,
      render: (_, row) => row.appellant_name || row.appellant_email || `#${row.user_id}`,
    },
    {
      title: 'Case',
      key: 'case',
      width: 160,
      render: (_, row) => (
        <span>
          #{row.case_id}
          {' '}
          <Tag>{row.case_subject_type || '—'}</Tag>
        </span>
      ),
    },
    { title: 'Reason', dataIndex: 'reason', key: 'reason', ellipsis: true },
    {
      title: 'Submitted',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 170,
      render: (d) => (d ? new Date(d).toLocaleString() : '—'),
    },
    {
      title: 'Reviewer',
      dataIndex: 'reviewer_name',
      key: 'reviewer_name',
      width: 140,
      render: (v) => v || '—',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            type="primary"
            disabled={record.status !== 'pending'}
            onClick={() => setReviewModal({
              appealId: record.id, status: 'approved', notes: '',
            })}
          >
            Approve
          </Button>
          <Button
            size="small"
            danger
            disabled={record.status !== 'pending'}
            onClick={() => setReviewModal({
              appealId: record.id, status: 'denied', notes: '',
            })}
          >
            Deny
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'flex-end' }}>
        <Select
          value={statusFilter}
          onChange={setStatusFilter}
          style={{ width: 160 }}
          options={[
            { value: '', label: 'All statuses' },
            { value: 'pending', label: 'Pending' },
            { value: 'approved', label: 'Approved' },
            { value: 'denied', label: 'Denied' },
          ]}
        />
      </div>
      {isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={error?.data?.title || 'Failed to load appeals'}
        />
      )}
      <div style={adminPanelStyle}>
        <Table
          dataSource={data?.items}
          columns={columns}
          loading={isLoading}
          rowKey="id"
          pagination={{ pageSize: 20 }}
          size="middle"
          expandable={{
            expandedRowRender: (row) => (
              <div style={{ fontSize: 13, color: 'var(--df-text-secondary)' }}>
                <div>
                  <strong>Appeal reason:</strong>
                  {' '}
                  {row.reason}
                </div>
                {row.case_reason && (
                  <div>
                    <strong>Case reason:</strong>
                    {' '}
                    {row.case_reason}
                  </div>
                )}
                {row.review_notes && (
                  <div>
                    <strong>Review notes:</strong>
                    {' '}
                    {row.review_notes}
                  </div>
                )}
              </div>
            ),
          }}
          locale={{ emptyText: <Empty description="No appeals" /> }}
        />
      </div>
      <Modal
        title={reviewModal?.status === 'approved' ? 'Approve appeal' : 'Deny appeal'}
        open={!!reviewModal}
        onOk={handleReview}
        onCancel={() => setReviewModal(null)}
        confirmLoading={reviewing}
        okText="Submit review"
        okButtonProps={{ danger: reviewModal?.status === 'denied' }}
      >
        <TextArea
          rows={3}
          placeholder="Review notes (optional)"
          value={reviewModal?.notes || ''}
          onChange={(e) => setReviewModal({ ...reviewModal, notes: e.target.value })}
        />
      </Modal>
    </>
  );
}

export default function ModerationPage() {
  return (
    <div>
      <AdminPageHeader
        title="Moderation"
        subtitle="Review reports and contested moderation decisions (appeals)"
      />
      <Tabs
        items={[
          { key: 'cases', label: 'Cases', children: <CasesPanel /> },
          { key: 'appeals', label: 'Appeals', children: <AppealsPanel /> },
        ]}
      />
    </div>
  );
}
